import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import moment from "moment";
import "moment-timezone";
import {formatDateTimeWithTimeZone} from "../Common/Common-Function/CommonFunctions";

export const DynamicContext = createContext();

const themeOptions = ["system", "light", "dark"];

const getStoredTheme = () => {
  const theme = localStorage.getItem("Theme");
  return themeOptions.includes(theme) ? theme : "light";
};

const getSystemTheme = () => {
  if (typeof window === "undefined" || !window.matchMedia) {
    return "light";
  }

  if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }

  return "light";
};

const getAppliedTheme = (theme) => {
  if (theme === "system") {
    return getSystemTheme();
  }

  return theme === "dark" ? "dark" : "light";
};

export const DynamicDataProvider = ({ children }) => {
  const storedCloseTab = localStorage.getItem("CloseTab") === "true";
  const storedUser = localStorage.getItem("user");
  const extractInitialUser = storedUser ? JSON.parse(storedUser) : null;
  const initialUser = extractInitialUser?.user
    ? extractInitialUser?.user
    : extractInitialUser;
  const storedTheme = getStoredTheme();
  const storedTenant = localStorage.getItem("tenant");
  const parseStoredTenant = storedTenant ? JSON.parse(storedTenant) : null;
  const extractTenant = parseStoredTenant?.tenant;

  const [themePreference, setThemePreference] = useState(storedTheme);
  const [isDarkMode, setIsDarkModeState] = useState(
    getAppliedTheme(storedTheme) === "dark",
  );
  const [closeTab, setCloseTab] = useState(storedCloseTab);
  const [skeletonLoading, setSkeletonLoading] = useState(false);
  const [userDetails, setUserDetails] = useState(initialUser);
  const [showResumeParser, setShowResumeParser] = useState(false);
  const [resetWebSocket, setResetWebSocket] = useState(false);
  const [isParsingComplete, setIsParsingComplete] = useState(false);
  const [UploadedResumes, setUploadedResumes] = useState([]);
  const [isUploadResumeViewOpen, setUploadResumeViewOpen] = useState(false);
  const [hideCandidatesHeader, setHideCandidatesHeader] = useState(true);
  const [feedbackScreenshot, setFeedbackScreenshot] = useState(null);
  const [assignedCount, setAssignedCount] = useState(0);
  const [dashboardSkeletonLoading, setDashboardSkeletonLoading] =
    useState(true);
  const [OpenFeedBack, setOpenFeedBack] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadInterruptPopup, setShowUploadInterruptPopup] =
    useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = useState(null);
  const [parsedFiles, setParsedFiles] = useState([]);
  const [failedFiles, setFailedFiles] = useState([]);
  const [incompleteFiles, setIncompleteFiles] = useState([]);
  const [isFromCandidateRecommendedJobs, setIsFromCandidateRecommendedJobs] =
    useState(false);
  const [isFromJobRecommendedCandidates, setIsFromJobRecommendedCandidates] =
    useState(false);
  const runningUploadsCount =
    UploadedResumes.length -
    (parsedFiles?.length || 0) -
    (failedFiles?.length || 0) -
    (incompleteFiles?.length || 0);
  const [isSignupCompleted, setSignupCompleted] = useState(false);
  const [memberAccountData, setMemberAccountData] = useState(null);
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [parsedData, setParsedData] = useState([]);
  const [incompleteData, setIncompleteData] = useState([]);
  const [runSetId, setRunSetId] = useState(null);
  const [errorMessages, setErrorMessages] = useState([]);
  const [successMessages, setSuccessMessages] = useState([]);
  const [showFailedTab, setShowFailedTab] = useState(false);
  const [dashboardMetrics, setDashboardMetrics] = useState({
    totalCandidates: null,
    createdByUser: null,
    activeJobs: null,
    trackedToday: null,
    totalAdminClient: null,
    candidatesCreatedToday: null,
  });
  const [dashboardHasAnimated, setDashboardHasAnimated] = useState(false);
  const [isPageReload, setIsPageReload] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [userData, setUserData] = useState({
    account: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      newPassword: "",
      confirmNewPassword: "",
    },
    companyDetails: {
      companyName: "",
      industry: "",
      companySize: "",
    },
    role: "",
    purpose: {
      reason: "",
    },
    discovery: {
      discover: "",
    },
    inviteTeamMembers: {
      emails: [],
    },
  });
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(true);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);
  const [isInitialSubscriptionFetchDone, setIsInitialSubscriptionFetchDone] =
    useState(false);
  const [subscriptionRefetchKey, setSubscriptionRefetchKey] = useState(0);
  const [tenantDetails, setTenantDetails] = useState(extractTenant);
  const [tempAdminSignupData, setTempAdminSignupData] = useState(null);
  const [showOtpPage, setShowOtpPage] = useState(false);
  const [spreadsheetResults, setSpreadsheetResults] = useState([]);
  const [failedSpreadsheets, setFailedSpreadsheets] = useState([]);
  const [showSpreadsheetResults, setShowSpreadsheetResults] = useState(false);
  const [userTimezone, setUserTimezone] = useState(() => {
    if (initialUser?.timezone && moment.tz.zone(initialUser.timezone)) {
      return initialUser.timezone;
    }
    return moment.tz.guess();
  });
  const [uploadType, setUploadType] = useState("resume");
  const [spreadsheetLoading, setSpreadsheetLoading] = useState(false);
  const [refetchCanList, setRefetchCanList] = useState(false);
  const [notificationsState, setNotificationsState] = useState({
    notifications: [],
    unread_count: 0,
  });
  const socketRef = useRef(null);
  const pingIntervalRef = useRef(null);

  useEffect(() => {
    if (userDetails?.timezone && moment.tz.zone(userDetails.timezone)) {
      setUserTimezone(userDetails.timezone);
    }
  }, [userDetails?.timezone]);

  const updateUserData = (section, data) => {
    setUserData((prevData) => {
      const newData = {
        ...prevData,
        [section]:
          section === "role" ? data : { ...prevData[section], ...data },
      };
      return newData;
    });
  };

  useEffect(() => {
    const hasReloaded = sessionStorage.getItem("dashboardAnimated");
    if (!hasReloaded) {
      setIsPageReload(true);
      sessionStorage.setItem("dashboardAnimated", "true");
    } else {
      setIsPageReload(false);
    }
  }, []);

  useEffect(() => {
    const applyTheme = () => {
      const theme = getAppliedTheme(themePreference);
      document.body.setAttribute("data-theme", theme);
      setIsDarkModeState(theme === "dark");
    };

    localStorage.setItem("Theme", themePreference);
    applyTheme();

    if (themePreference !== "system" || !window.matchMedia) {
      return undefined;
    }

    const systemThemeQuery = window.matchMedia("(prefers-color-scheme: dark)");
    systemThemeQuery.addEventListener("change", applyTheme);

    return () => {
      systemThemeQuery.removeEventListener("change", applyTheme);
    };
  }, [themePreference]);

  const setIsDarkMode = (isDark) => {
    setThemePreference(isDark ? "dark" : "light");
  };

  useEffect(() => {
    localStorage.setItem("closeTab", closeTab);
  }, [closeTab]);

  useEffect(() => {
    if (userDetails) {
      localStorage.setItem(
        "user",
        JSON.stringify({
          ...extractInitialUser,
          user: userDetails,
        }),
      );
    }
  }, [userDetails]);

  useEffect(() => {
    if (tenantDetails) {
      localStorage.setItem(
        "tenant",
        JSON.stringify({
          ...parseStoredTenant,
          tenant: tenantDetails,
        }),
      );
    }
  }, [tenantDetails]);

  const triggerWebSocketReset = () => {
    setResetWebSocket(true);
    setTimeout(() => setResetWebSocket(false), 100);
  };

  const resetUploadState = () => {
    setShowResumeParser(false);
  };

  const isVendorFeatureEnabled = useMemo(() => {
    if (!subscriptionDetails?.features) return false;
    const vendorFeature = subscriptionDetails.features.find(
      (feature) => feature.name === "vendor",
    );
    return vendorFeature?.toggle === true;
  }, [subscriptionDetails]);

  const isVendorIntegrationsEnabled = useMemo(() => {
    if (!subscriptionDetails?.features) return false;
    const vendorIntegrationsFeature = subscriptionDetails.features.find(
      (feature) => feature.name === "vendor_integrations",
    );
    return vendorIntegrationsFeature?.toggle === true;
  }, [subscriptionDetails]);

  const monthlyEmailsFeature = useMemo(() => {
    if (!subscriptionDetails?.features) return null;
    return subscriptionDetails.features.find(
      (feature) => feature.name === "monthly_emails",
    );
  }, [subscriptionDetails]);

  const isMonthlyEmailLimitReached = useMemo(() => {
    if (!monthlyEmailsFeature) return false;

    const used = Number(monthlyEmailsFeature.used);
    const limit = Number(monthlyEmailsFeature.limit);

    if (monthlyEmailsFeature.toggle === false) return true;
    if (!Number.isFinite(used) || !Number.isFinite(limit) || limit < 0) {
      return false;
    }

    return used >= limit;
  }, [monthlyEmailsFeature]);

  const getMonthlyEmailTooltip = (activeTitle) => {
    if (isMonthlyEmailLimitReached) return "Your Monthly email limit reached";
    if (!monthlyEmailsFeature) return activeTitle;

    const used = Number(monthlyEmailsFeature.used);
    const limit = Number(monthlyEmailsFeature.limit);

    if (monthlyEmailsFeature.toggle === false) {
      return "Your Monthly email limit reached";
    }
    if (!Number.isFinite(limit)) return activeTitle;
    if (limit < 0) return "Monthly emails: Unlimited";

    const safeUsed = Number.isFinite(used) ? used : 0;
    const remaining = Math.max(limit - safeUsed, 0);
    return `Total emails: ${limit}, Remaining emails: ${remaining}`;
  };

  const MemberAccess = userDetails?.role === "MEMBER";
  const AdminAccess =
    userDetails?.role === "ADMIN" || userDetails?.role === "MANAGER";
  const UnAssociatedAccess = userDetails?.role === "UNASSOCIATED";
  const SuperAdminAccess = userDetails?.role === "SYSTEM_ADMIN";
const HIRING_TEAM_TENANT_ID = "036b31a5fa6a4f11b6601718903ffab9";
  const isHiringTeamTenant = userDetails?.tenant_id === HIRING_TEAM_TENANT_ID;

  const formatDateWithUserTimezone = useCallback(
    (date) => formatDateTimeWithTimeZone(date, userTimezone),
    [userTimezone],
  );
  return (
    <DynamicContext.Provider
      value={{
        closeTab,
        setCloseTab,
        themePreference,
        setThemePreference,
        isDarkMode,
        setIsDarkMode,
        skeletonLoading,
        setSkeletonLoading,
        userDetails,
        setUserDetails,
        setShowResumeParser,
        showResumeParser,
        resetUploadState,
        triggerWebSocketReset,
        resetWebSocket,
        setResetWebSocket,
        isParsingComplete,
        setIsParsingComplete,
        isUploadResumeViewOpen,
        setUploadResumeViewOpen,
        hideCandidatesHeader,
        setHideCandidatesHeader,
        UploadedResumes,
        setUploadedResumes,
        OpenFeedBack,
        setOpenFeedBack,
        feedbackScreenshot,
        setFeedbackScreenshot,
        dashboardSkeletonLoading,
        setDashboardSkeletonLoading,
        isUploading,
        setIsUploading,
        showUploadInterruptPopup,
        setShowUploadInterruptPopup,
        pendingNavigationPath,
        setPendingNavigationPath,
        parsedFiles,
        setParsedFiles,
        failedFiles,
        setFailedFiles,
        runningUploadsCount,
        isFromCandidateRecommendedJobs,
        setIsFromCandidateRecommendedJobs,
        isFromJobRecommendedCandidates,
        setIsFromJobRecommendedCandidates,
        incompleteFiles,
        setIncompleteFiles,
        isSignupCompleted,
        setSignupCompleted,
        userData,
        updateUserData,
        memberAccountData,
        setMemberAccountData,
        signupPassword,
        setSignupPassword,
        signupEmail,
        setSignupEmail,
        parsedData,
        setParsedData,
        incompleteData,
        setIncompleteData,
        runSetId,
        setRunSetId,
        errorMessages,
        setErrorMessages,
        successMessages,
        setSuccessMessages,
        showFailedTab,
        setShowFailedTab,
        subscriptionDetails,
        setSubscriptionDetails,
        hasSubscription,
        setHasSubscription,
        isSubscriptionLoading,
        setIsSubscriptionLoading,
        isInitialSubscriptionFetchDone,
        setIsInitialSubscriptionFetchDone,
        isVendorFeatureEnabled,
        isVendorIntegrationsEnabled,
        monthlyEmailsFeature,
        isMonthlyEmailLimitReached,
        getMonthlyEmailTooltip,
        dashboardMetrics,
        setDashboardMetrics,
        dashboardHasAnimated,
        setDashboardHasAnimated,
        isPageReload,
        setIsPageReload,
        sessionExpired,
        setSessionExpired,
        tenantDetails,
        setTenantDetails,
        tempAdminSignupData,
        setTempAdminSignupData,
        assignedCount,
        setAssignedCount,
        userTimezone,
        setUserTimezone,
        showOtpPage,
        setShowOtpPage,
        spreadsheetResults,
        setSpreadsheetResults,
        failedSpreadsheets,
        setFailedSpreadsheets,
        showSpreadsheetResults,
        setShowSpreadsheetResults,
        uploadType,
        setUploadType,
        spreadsheetLoading,
        setSpreadsheetLoading,
        socketRef,
        pingIntervalRef,
        refetchCanList,
        setRefetchCanList,
        notificationsState,
        setNotificationsState,
        MemberAccess,
        AdminAccess,
        UnAssociatedAccess,
        SuperAdminAccess,
        subscriptionRefetchKey,
        setSubscriptionRefetchKey,
        isHiringTeamTenant,
        formatDateWithUserTimezone,
      }}
    >
      {children}
    </DynamicContext.Provider>
  );
};
